import * as React from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useUiStore } from '@/store/uiStore';
import { Button } from '@/components/ui/Button';
import { GradientModal } from '@/components/ui/GradientModal';
import logoVarTwo from '@/assets/images/svg/logo.svg';

/**
 * Displays the "Become a Creator" terms and benefits.
 * Entry point for the Creator Onboarding wizard.
 */
export const CreatorModal: React.FC = () => {
  const isOpen = useUiStore((state) => state.modals.creator);
  const closeModal = useUiStore((state) => state.closeModal);
  const navigate = useNavigate();

  const handleAccept = () => {
    closeModal('creator');
    navigate('/create-echo');
  };

  return (
    <GradientModal isOpen={isOpen} onClose={() => closeModal('creator')} variant='purple'>
      <div className='mb-8 flex flex-col justify-start gap-4 md:flex-row'>
        <div className='flex flex-col items-center gap-3 text-center md:flex-row md:items-center'>
          <span className='font-sans text-32 font-bold leading-27 text-brand-800'>Become a</span>
          <img src={logoVarTwo} alt='d.echo' className='mt-2 h-[81px] w-[144px] object-contain' />
          <span className='font-sans text-32 font-bold leading-27 text-brand-800'>Creator</span>
        </div>
      </div>

      <div className='space-y-8'>
        <section>
          <h3 className='mb-3 font-sans text-20 font-medium leading-27 text-modal-text'>
            Who are we?
          </h3>
          <p className='font-sans text-16 font-normal leading-24 text-modal-body'>
            We, D.ECHO, have developed a multi-agent AI platform that creates interactive digital
            twins of athletes and celebrities. The D.ECHO application is a powerful tool that can be
            used both as support for training sessions, and for fans who want to help engage with
            their heroes at home. The D.ECHO platform is a digital clinic through which you have
            access to a legendary athlete or celebrity anytime, anywhere! Like an athlete or
            celebrity – this is your virtual office with everything you need.
          </p>
        </section>

        <section>
          <h3 className='mb-3 font-sans text-20 font-medium leading-27 text-modal-text'>
            Why do we need such data and how do we use it?
          </h3>
          <p className='mb-3 font-sans text-16 font-normal leading-24 text-modal-body'>
            Without such data, our solutions would not work! Therefore, we use such data for the
            following purposes:
          </p>
          <ul className='list-disc space-y-1 pl-5 font-sans text-16 font-normal leading-24 text-modal-body marker:text-modal-body'>
            <li>Registration on the platform.</li>
            <li>Closing and executing the electronic contract between us (access to services).</li>
            <li>To offer you access to various facilities or materials.</li>
            <li>To manage the relationship we have with you and to offer you assistance.</li>
            <li>
              Analytical and statistical purposes, to improve the functionality and user experience.
            </li>
            <li>
              For marketing purposes, if we have your consent or if you are our beneficiary and the
              information may be of great interest to you.
            </li>
          </ul>
        </section>

        <section>
          <h3 className='mb-3 font-sans text-20 font-medium leading-27 text-modal-text'>
            As a D.ECHO Creator, you will unlock:
          </h3>
          <ul className='list-disc space-y-1 pl-5 font-sans text-16 font-normal leading-24 text-modal-body marker:text-modal-body'>
            <li>Connect with fans globally in real-time through your digital twin.</li>
            <li>Build a lasting legacy that extends your impact beyond your active career.</li>
            <li>Generate revenue from fan interactions 24/7 while you focus on your craft.</li>
            <li>Own your digital likeness and control how it engages with the world.</li>
            <li>Access comprehensive analytics and insights about your fan engagement.</li>
            <li>Create your own digital personality which can interact with users continuously.</li>
          </ul>
        </section>

        <section>
          <h3 className='mb-2 font-sans text-20 font-medium leading-27 text-modal-text'>
            Important Note
          </h3>
          <p className='font-sans text-16 font-normal leading-24 text-modal-body'>
            You can deactivate your creator status at any time from your profile.
            <br />
            Your data will be preserved if you decide to return.
          </p>
        </section>
      </div>

      <div className='mt-10 flex items-center justify-between'>
        <button
          type='button'
          onClick={() => closeModal('creator')}
          className='font-sans text-16 font-semibold leading-27 text-black transition-opacity hover:opacity-70'
        >
          Refuse
        </button>

        <Button
          onClick={handleAccept}
          variant='sidebar-default'
          className={clsx(
            'h-[44px] rounded-30 px-10',
            'font-sans text-16 font-semibold leading-27',
            'text-brand-800'
          )}
        >
          Accept
        </Button>
      </div>
    </GradientModal>
  );
};
